// TS mirror of the backend `app/services/llm/functions/actions.py::A` action types.
// SAM tools publish `sam:action` events; the dispatcher (useSamActions) maps these to
// the dynamic workspace. Mapping is by suffix/keyword so new modules need no new branch.

export type SamModule =
  | 'tasks'
  | 'notes'
  | 'properties'
  | 'meetings'
  | 'transactions'
  | 'proposals'
  | 'jobs'
  | 'calendar'
  | 'billing'
  | 'notifications'
  | 'users'
  | 'roles'
  | 'communications'
  | 'my_todos'
  | 'workflow'
  | 'engagements'
  // PCR-FEAT-02 / 03 slices — each has a workspace panel and (planned) detail cards.
  // The backend already publishes OPEN_/REFRESH_ actions with these module values.
  | 'contacts'
  | 'warranties'
  | 'measurements'
  | 'media'
  // Quick Quote — a satellite-map + drawing-tools workspace card for a roof estimate. No
  // persisted record (no REST endpoint); the entire payload rides on the QUICK_QUOTE action.
  | 'quick_quote'
  // EST-FEAT-01/02/03 — assessment scheduling and capture.
  | 'assessments';

/** SAM-authored communication draft carried by a DRAFT_COMMUNICATION action. The user reviews and
 *  sends it from the ComposeCard — SAM never sends directly. */
export interface SamCommunicationDraft {
  channel: 'email' | 'sms' | 'in_app';
  recipients: Array<{
    recipient_type: string;
    user_id?: string;
    contact_id?: string;
    name?: string;
    email?: string;
    phone?: string;
  }>;
  subject: string;
  body: string;
  context: { context_type: string; entity_id: string; display_label?: string | null } | null;
}

/** SAM-authored Quick Quote payload carried by a QUICK_QUOTE action — an address + geocoded
 *  location, plus Google Solar API's AI roof-area estimate when available. There is no persisted
 *  record and no REST endpoint: the entire payload rides on this action. The backend degrades
 *  gracefully when Solar has no data for the address (geocode succeeded, Solar didn't) —
 *  `ai_estimate_available: false` — so the UI must omit the AI estimate section, not error. */
/** One whole-number pitch bucket's share of the roof, area-weighted (backend: solar.py). */
export interface SamPitchBreakdownRow {
  pitch: string;
  rise_per_12: number;
  area_sqft: number;
  squares: number;
  share_pct: number;
}

export interface SamQuickQuotePayload {
  address: string;
  lat: number;
  lng: number;
  ai_roof_area_m2: number | null;
  /** SLOPED (actual surface) area — pitch already accounted for by the Solar API. */
  ai_roof_area_sqft: number | null;
  ai_squares: number | null;
  ai_estimate_available: boolean;
  /** FLAT footprint area — what tracing the top-down satellite image actually measures. */
  ai_ground_area_sqft?: number | null;
  /** Area-weighted predominant pitch, e.g. "8/12". Derived from the Solar API's per-facet 3D
   *  data — a flat overhead image alone cannot yield pitch. */
  predominant_pitch?: string | null;
  predominant_pitch_degrees?: number | null;
  /** Footprint → sloped-area factor for the predominant pitch (8/12 → 1.202). */
  pitch_multiplier?: number | null;
  pitch_breakdown?: SamPitchBreakdownRow[];
  segment_count?: number;
  /** Date of the satellite imagery the estimate was derived from — an old capture can predate
   *  a remodel and is the usual reason for a gap vs. a fresh professional aerial report. */
  imagery_date?: string | null;
  /** Ballpark cost — tenant's quick_quote_pricing config × measured area (app.services.pricing.
   *  quick_quote). All null/undefined when area or pricing isn't available — never a fabricated
   *  number. `estimated_cost_low` is quantity × price_per_unit; `estimated_cost_high` applies
   *  `complexity_factor` on top. */
  pricing_unit?: 'sqft' | 'square' | 'sqm' | null;
  price_per_unit?: number | null;
  complexity_factor?: number | null;
  billable_quantity?: number | null;
  estimated_cost_low?: number | null;
  estimated_cost_high?: number | null;
}

/** A backend-computed filter chip shown above a card list (e.g. "Open (2)"). */
export interface SamFacet {
  key: string;
  label: string;
  count: number;
  /** kwargs the backend re-runs the list tool with when this chip is clicked. */
  args: Record<string, unknown>;
}

/** One action item inside a `sam:action` envelope. */
/** One editable field on a confirmation card. `arg` is a dotted path into the tool's arguments;
 *  an edit goes back with Confirm and the backend re-runs the tool with it. */
export interface SamEditableField {
  arg: string;
  label: string;
  type: 'text' | 'textarea' | 'number' | 'date' | 'datetime' | 'select';
  options?: string[];
  required?: boolean;
  value?: unknown;
}

export interface SamAction {
  /** SHOW_FEEDBACK (card rulebook toast). */
  level?: string;
  message_code?: number;
  duration_ms?: number;
  /** SHOW_FEEDBACK i18n interpolation values (entity, title, detail — see messages 4335-4337). */
  params?: Record<string, string | number>;
  action_type: string;
  module?: SamModule;
  /** Card-catalogue id this action belongs to (`task.list`, `engagement.view`, …) — stamped by the
   *  backend's publish_action; absent on view-control actions. */
  card_definition_id?: string;
  filter?: Record<string, unknown>;
  prefill?: Record<string, unknown>;
  record_id?: string;
  /** Labels for the workspace "⋯" recent-views entry ONLY — never the card header or an inline
   *  chat card. A write that lands the user on a surface (create_task / create_meeting) uses these
   *  to name its menu entry after the record it just created, without gaining a chat card the way
   *  passing `title`/`caption` would. See `publish_action(view_title=…)`. */
  view_title?: string;
  view_caption?: string;
  /** REPLAY_RECENT_VIEW only — the stored entry's `context_ref.type` (e.g. `workflow_settings`,
   *  `company_workflows`), for surfaces with no natural `record_id` where `contextRefFor(module,
   *  recordId)` can't reconstruct the type on its own. */
  context_ref_type?: string;
  /** 0-based card index for OPEN_CARD navigation (e.g. "open 1st task" → index 0). */
  index?: number;
  count?: number;
  records?: Record<string, unknown>[];
  /** Backend-computed filter chips for the card list. */
  facets?: SamFacet[];
  // persistent action-card fields (panel/list reads) — LLM-authored heading + summary.
  title?: string;
  caption?: string;
  // confirmation-card fields (action_type === 'confirmation')
  call_id?: string;
  tool_name?: string;
  tool_args?: Record<string, unknown>;
  /** When the backend stops waiting for the decision (epoch ms) — after it the card is expired. */
  expires_at?: number;
  /** How long the backend waits in total (sam_tool_confirmation.timeout_seconds). */
  timeout_seconds?: number;
  /** Fields the user may correct before confirming (the tool's catalogue `confirmation_fields`). */
  editable?: SamEditableField[];
  // confirmation-result fields (action_type === 'confirmation_result') — reports what actually
  // happened after the user approved a guardrailed tool, keyed back to the card via call_id.
  status?: 'success' | 'partial_success' | 'failed' | 'cancelled';
  status_message?: string;
  // The tool's returned `data` dict (confirmation_result only) — e.g. create_engagement's
  // active_engagement_exists conflict payload.
  data?: Record<string, unknown> | null;
  // view-control fields (SWITCH_VIEW / NAVIGATE_CARDS)
  /** SWITCH_VIEW target surface. */
  target?: 'chat' | 'workspace';
  /** NAVIGATE_CARDS relative step; NAVIGATE_CALENDAR also uses 'today'. */
  direction?: string;
  /** NAVIGATE_CARDS absolute 1-based card number to jump to. */
  position?: number;
  /** NAVIGATE_CALENDAR: period unit 'day' | 'week' | 'month' */
  unit?: string;
  /** NAVIGATE_CALENDAR: switch calendar view 'day' | 'week' | 'month' */
  view?: string;
  /** NAVIGATE_CALENDAR: jump to specific YYYY-MM-DD date */
  date?: string;
  /** DRAFT_COMMUNICATION payload — a reviewable email/SMS/in-app draft. */
  draft?: SamCommunicationDraft;
  /** QUICK_QUOTE payload — address + geocode + AI roof-area estimate (when available). */
  quick_quote?: SamQuickQuotePayload;
  /** NAVIGATE_ROUTE frontend path to push (e.g. "/tasks/new", "/tasks/abc123"). */
  path?: string;
  /** OPEN_NOTE_TAKER optional business-context link for the new note. */
  context_type?: string;
  entity_id?: string;
  /** EXPAND_ENGAGEMENT_QUICK_QUOTE / REFRESH_QUICK_QUOTE / OPEN_QUICK_QUOTE_MAP /
   *  EXPAND_ENGAGEMENT_TRANSITION_CAPTURE — the engagement to act on. */
  engagement_id?: string;
  /** EXPAND_ENGAGEMENT_TRANSITION_CAPTURE — the blocked transition whose business_capture form
   *  to open (matches AvailableTransition.transition_code on the WorkflowCard already showing). */
  transition_code?: string;
  /** OPEN_ENGAGEMENT_INVOICE — the invoice to unfold on the engagement's Billing panel. */
  invoice_id?: string;
  /** OPEN_ENGAGEMENT_SITE_BOARD — optional post to bring into view on the job's Site Board. */
  entry_id?: string;
  /** PROPOSAL_SURFACE_INTERACT — see ProposalSurfaceInteractPayload for the full shape. */
  surface?: string;
  action?: string;
  option_id?: string;
  section?: string;
  expanded?: boolean;
  item_id?: string;
  /** OPEN_QUICK_QUOTE_MAP — when true, also open the satellite map inside the Quick Quote section. */
  open_map?: boolean;
  /** SET_NOTE_FOLLOWUP_INCLUDED — one or more suggestion_ids to check/uncheck; omit both to
   *  apply to every pending suggestion on the note (select all / unselect all). */
  suggestion_id?: string;
  suggestion_ids?: string[];
  /** SET_NOTE_FOLLOWUP_INCLUDED — the checked state to apply. */
  included?: boolean;
  /** EXPAND_NOTE_FOLLOWUP — optional field edits to apply while opening the follow-up for review. */
  fields?: Record<string, unknown>;
}

/** One card lifecycle event (cards_version 2) — additive alongside `actions[]`, one per card the
 *  envelope touches. `card_instance_id` is set on `card.open` only (stable per session + card +
 *  record + filter). */
export interface SamCardEvent {
  type: 'card.open' | 'card.refresh' | 'card.signal';
  /** cards_version 3 — alias of card_definition_id. */
  card?: string;
  card_definition_id: string;
  /** cards_version 3 — every data field of the publish (filter, record_id, tab, facets, target, …). */
  args?: Record<string, unknown>;
  /** cards_version 3 — card.signal only: lower-cased action type, e.g. "navigate_calendar". */
  command?: string;
  /** cards_version 3 — card.open only: "⋯" recent-views labels. */
  view_title?: string | null;
  view_caption?: string | null;
  card_instance_id: string | null;
  action_types: string[];
  record_id: string | null;
  semantic_state: {
    filter: Record<string, unknown>;
    record_id: string | null;
    parent_context: { type: string; id: string } | null;
  };
  title?: string | null;
  caption?: string | null;
}

/** How the card rulebook wants a temporary surface shown (SHOW_TEMPORARY_UI `interaction`) — today
 *  only a confirmation card: `{kind: 'CONFIRMATION', presentation: 'OVERLAY', blocking: true}`. */
export interface SamInteraction {
  kind?: string;
  presentation?: string;
  blocking?: boolean;
}

export interface SamPresentation {
  decision?: string;
  rule_id?: string;
  policy_version?: number;
  surface_hint?: string;
  interaction?: SamInteraction;
}

/** The `agent:action` socket payload — `actions[]` (CRM) or singular `action` (confirmation). */
export interface SamActionEnvelope {
  actions?: SamAction[];
  action?: SamAction;
  /** cards_version 2 — card lifecycle events for the same actions. */
  card_events?: SamCardEvent[];
  session_id?: string;
  message_id?: string;
  /** The api-service trace run this envelope belongs to — acked via card.presentation_applied. */
  run_id?: string | null;
  /** The policy decision this envelope was emitted under (cards/policy.py) — echoed in the ack. */
  presentation?: SamPresentation | null;
  source?: string;
}

export const CONFIRMATION = 'confirmation';
/** Reports the real outcome of a guardrailed tool call after the user approved it — keyed back
 *  to the ConfirmCard via call_id so it can show the actual result instead of just the click. */
export const CONFIRMATION_RESULT = 'confirmation_result';
export const OPEN_CARD = 'OPEN_CARD';
export const CLOSE_CARD = 'CLOSE_CARD';
export const SWITCH_VIEW = 'SWITCH_VIEW';
export const NAVIGATE_CARDS = 'NAVIGATE_CARDS';
export const OPEN_NOTE_TAKER = 'OPEN_NOTE_TAKER';
export const STOP_NOTE_TAKER = 'STOP_NOTE_TAKER';
/** Trigger the "Create all selected" batch on the open note review card. */
export const EXECUTE_NOTE_FOLLOWUPS = 'EXECUTE_NOTE_FOLLOWUPS';
/** Check/uncheck one, several, or all follow-up suggestions on the open note review card. */
export const SET_NOTE_FOLLOWUP_INCLUDED = 'SET_NOTE_FOLLOWUP_INCLUDED';
/** Expand a follow-up suggestion's review/edit panel (no create) — mirrors clicking "Review". */
export const EXPAND_NOTE_FOLLOWUP = 'EXPAND_NOTE_FOLLOWUP';
export const DRAFT_COMMUNICATION = 'DRAFT_COMMUNICATION';
/** SAM drafted a Quick Quote (satellite map + drawing tools) for an address's roof estimate. */
export const QUICK_QUOTE = 'QUICK_QUOTE';
/** SAM opened the Quick Quote section embedded in an engagement's WorkflowCard. */
export const EXPAND_ENGAGEMENT_QUICK_QUOTE = 'EXPAND_ENGAGEMENT_QUICK_QUOTE';
/** SAM's get_job_card — unfold the Job Card on that engagement's WorkflowCard (carries engagement_id). */
export const EXPAND_ENGAGEMENT_JOB_CARD = 'EXPAND_ENGAGEMENT_JOB_CARD';
/** SAM's get_job_card(download=true) — download that engagement's Job Card PDF (carries engagement_id). */
export const DOWNLOAD_ENGAGEMENT_JOB_CARD = 'DOWNLOAD_ENGAGEMENT_JOB_CARD';
/** SAM's get_job_card(section=...) — open one Job Card popup (proposal | assessment | materials | notes). */
export const OPEN_ENGAGEMENT_JOB_CARD_SECTION = 'OPEN_ENGAGEMENT_JOB_CARD_SECTION';
/** SAM's get_job_card(close=true) — close whichever Job Card popup is open. */
export const CLOSE_ENGAGEMENT_JOB_CARD_SECTION = 'CLOSE_ENGAGEMENT_JOB_CARD_SECTION';
/** SAM's get_invoice / invoice writes — unfold that invoice on the engagement's Billing panel (engagement_id + invoice_id). */
export const OPEN_ENGAGEMENT_INVOICE = 'OPEN_ENGAGEMENT_INVOICE';
/** An invoice changed through SAM — refetch that engagement's Billing panel invoices (engagement_id). */
export const REFRESH_BILLING = 'REFRESH_BILLING';
/** The job's Material List changed through SAM — refetch that engagement's Job Card only (engagement_id). */
export const REFRESH_JOB_CARD = 'REFRESH_JOB_CARD';
/** SAM's get_site_board / Site Board writes — bring the job's Site Board into view (engagement_id, entry_id?). */
export const OPEN_ENGAGEMENT_SITE_BOARD = 'OPEN_ENGAGEMENT_SITE_BOARD';
/** A Site Board post changed through SAM — refetch that engagement's feed (engagement_id). */
export const REFRESH_SITE_BOARD = 'REFRESH_SITE_BOARD';
/** SAM saved a Quick Quote — invalidate the RTK Query cache so the card auto-refetches. */
export const REFRESH_QUICK_QUOTE = 'REFRESH_QUICK_QUOTE';
/** A blocked transition's business_capture form (e.g. schedule_assessment) should expand on the
 *  WorkflowCard — mirrors clicking the blocked transition's button. */
export const EXPAND_ENGAGEMENT_TRANSITION_CAPTURE = 'EXPAND_ENGAGEMENT_TRANSITION_CAPTURE';
/** SAM was asked to add a photo/voice note/report/note it cannot itself accept — expand + flash
 *  the Assessment meeting & capture card so the user can find the real button. No blocked
 *  transition is involved here (contrast EXPAND_ENGAGEMENT_TRANSITION_CAPTURE). */
export const EXPAND_ENGAGEMENT_ASSESSMENT_CAPTURE = 'EXPAND_ENGAGEMENT_ASSESSMENT_CAPTURE';
/** SAM asked to open the satellite map inside the Quick Quote section on an engagement's WorkflowCard. */
export const OPEN_QUICK_QUOTE_MAP = 'OPEN_QUICK_QUOTE_MAP';
/** Navigate to a frontend route (e.g. /tasks/new or /tasks/:id). */
export const NAVIGATE_ROUTE = 'NAVIGATE_ROUTE';
/** Switch the active tab in the open task detail panel. */
/** Switch the active tab in the open meeting detail panel. */
/** Invalidate the RTK Query comments cache for a specific task. */
export const REFRESH_TASK_COMMENTS = 'REFRESH_TASK_COMMENTS';
/** Invalidate the RTK Query cache for a specific task detail (e.g. after update_task). */
export const REFRESH_TASK_DETAIL = 'REFRESH_TASK_DETAIL';
/** Invalidate the RTK Query cache for a specific meeting detail (after reschedule/cancel/add_meeting_note
 *  — those tools hit the backend directly via MeetingService, bypassing the frontend's own RTK
 *  mutations, so nothing else invalidates the open detail's cache). */
export const REFRESH_MEETING_DETAIL = 'REFRESH_MEETING_DETAIL';
/** Navigate the on-screen calendar (next/prev day/week/month, jump to date, switch view). */
export const NAVIGATE_CALENDAR = 'NAVIGATE_CALENDAR';
/** Open the single-engagement Workflow surface (get_engagement_workflow_state / create_engagement).
 *  Also reused (with module: 'workflow', no record_id) by list_pending_approvals for the
 *  approvals-only view — see OPEN_ENGAGEMENTS_PANEL below. */
export const OPEN_WORKFLOW_PANEL = 'OPEN_WORKFLOW_PANEL';
/** Re-fetch the open Workflow surface's state/transitions/approvals (after execute_workflow_transition
 *  / decide_workflow_approval — those hit the workflow service directly, bypassing the frontend's own
 *  RTK mutations, so nothing else invalidates the open surface's cache). */
export const REFRESH_WORKFLOW = 'REFRESH_WORKFLOW';
/** Open the Engagements list panel (search_engagement: module 'engagements'). Also reused by
 *  list_pending_approvals with module 'workflow' for the approvals-only Workflow surface. */
export const OPEN_ENGAGEMENTS_PANEL = 'OPEN_ENGAGEMENTS_PANEL';
/** Apply a filter to the open Engagements list panel. */
export const APPLY_ENGAGEMENTS_FILTER = 'APPLY_ENGAGEMENTS_FILTER';
/** Re-fetch the Engagements list panel. */
export const REFRESH_ENGAGEMENTS = 'REFRESH_ENGAGEMENTS';
/** Open the Properties list panel (search_engagement / create_property: module 'properties'). */
export const OPEN_PROPERTIES_PANEL = 'OPEN_PROPERTIES_PANEL';
/** Apply a filter to the open Properties list panel. */
export const APPLY_PROPERTIES_FILTER = 'APPLY_PROPERTIES_FILTER';
/** Re-fetch the Properties list panel (after create_property / update_property). */
export const REFRESH_PROPERTIES = 'REFRESH_PROPERTIES';
/** Invalidate RTK Query cache for one open property detail (record_id = property_id) — e.g. after
 *  execute_workflow_transition changes that property's active engagement's stage/status, so the
 *  panel reflects it without the user leaving and reopening the card. */
export const REFRESH_PROPERTY_DETAIL = 'REFRESH_PROPERTY_DETAIL';
/** Open the Workflow SETTINGS surface (get_workflow_customization) — module: 'workflow' (reused),
 *  but a DISTINCT surface from OPEN_WORKFLOW_PANEL: the tenant's whole workflow STRUCTURE (all
 *  stages -> processes -> statuses -> transitions), no engagement in the picture. Distinguished
 *  from OPEN_WORKFLOW_PANEL/OPEN_ENGAGEMENTS_PANEL by this literal action_type string, not just
 *  module + `_PANEL` suffix — see isWorkflowModuleAction's comment in useSamActions. */
export const OPEN_WORKFLOW_SETTINGS_PANEL = 'OPEN_WORKFLOW_SETTINGS_PANEL';
/** Re-fetch the open Workflow Settings surface (after toggle_workflow_item / rename_workflow_label /
 *  add_workflow_stage / add_workflow_process / add_workflow_status / add_workflow_transition /
 *  set_transition_authority — those hit the customization service directly, bypassing the
 *  frontend's own RTK mutations). */
export const REFRESH_WORKFLOW_SETTINGS = 'REFRESH_WORKFLOW_SETTINGS';
/** Open the Company Processes (workflow list) card — first step when tenant_admin asks to
 *  configure their company processes (list_company_workflows tool). After the user clicks
 *  "Customize", the WorkflowSettingsCard opens. Distinct from OPEN_WORKFLOW_SETTINGS_PANEL
 *  (which goes straight to the editor); this one shows the selectable overview first. */
export const OPEN_COMPANY_WORKFLOWS_PANEL = 'OPEN_COMPANY_WORKFLOWS_PANEL';

/** Proposal Prepared stage — open one of the three read-only panels on the compact card.
 *  `view` is "preview_proposal" | "view_estimate" | "open_assessment".
 *  `record_id` / `engagement_id` scope it to the right WorkflowCard instance. */
export const OPEN_PROPOSAL_PREPARED_VIEW = 'OPEN_PROPOSAL_PREPARED_VIEW';

/** Interact INSIDE an already-open Proposal Prepared surface (proposal_surface_interact tool):
 *  switch the estimate's active option tab, expand/collapse Materials or Work & Other Services,
 *  open an item's detail row, close the surface, download the PDF, or scroll it. `surface` is
 *  "preview_proposal" | "view_estimate" | "open_assessment"; `action` is "select_option" |
 *  "expand_section" | "collapse_section" | "open_item" | "close" | "download" | "scroll_top" |
 *  "scroll_bottom" | "scroll_up" | "scroll_down" | "scroll_to_section". View-state plus the one
 *  read-only PDF download — never mutates proposal/estimate data. */
export const PROPOSAL_SURFACE_INTERACT = 'PROPOSAL_SURFACE_INTERACT';

/** Live screen-awareness snapshot of the Proposal Prepared surfaces, pushed to the realtime
 *  session on every change — a SAM action or a manual click, either way — via
 *  emitProposalSurfaceContext (mirrors emitWorkflowContext/emitTaskContext's own pattern). Keeps
 *  "what am I looking at" answerable even when the user drives the UI by hand. */
export interface ProposalSurfaceContext {
  engagement_id: string;
  preview_open: boolean;
  estimate_open: boolean;
  estimate_option_id?: string;
  estimate_option_label?: string;
  materials_expanded: boolean;
  services_expanded: boolean;
  assessment_open: boolean;
  /** Everything visible in the estimate editor right now — the commercial totals and every
   *  material/service line's name and amount. No filtering: whatever the user can see, this
   *  carries, so a follow-up question never needs SAM to re-fetch it. */
  estimate_totals?: {
    currency: string; material_cost?: number | null; work_cost?: number | null;
    direct_cost?: number | null; discount_amount?: number | null; grand_total?: number | null;
  };
  estimate_materials?: Array<{ name: string; amount: number | null }>;
  estimate_services?: Array<{ name: string; amount: number | null }>;
}

export interface ProposalSurfaceInteractPayload {
  engagement_id: string;
  surface: 'preview_proposal' | 'view_estimate' | 'open_assessment';
  action: 'select_option' | 'expand_section' | 'collapse_section' | 'open_item' | 'close' | 'download'
    | 'scroll_top' | 'scroll_bottom' | 'scroll_up' | 'scroll_down' | 'scroll_to_section';
  option_id?: string;
  section?: string;
  expanded?: boolean;
  item_id?: string;
}

/** Reopen a workspace "⋯" recent-views entry (list_recent_views / open_recent_view tools).
 *  Module-agnostic on purpose: `module`/`filter`/`record_id`/`title`/`caption` on the action carry
 *  everything `useChatWorkspace.replayView` needs to dispatch to the right surface — the backend
 *  never re-derives a module-specific OPEN_*_PANEL/OPEN_*_DETAIL pair for the target. */
export const REPLAY_RECENT_VIEW = 'REPLAY_RECENT_VIEW';
/** The turn put nothing on screen (lookups withheld / no match) — whatever card is showing belongs
 *  to an earlier question. No visual handling yet; acked as ignored so the card trace shows it. */
export const NO_VIEW_THIS_TURN = 'NO_VIEW_THIS_TURN';
/** Card-rulebook toast: { level: INFO|SUCCESS|WARNING, message_code, duration_ms }. */
export const SHOW_FEEDBACK = 'SHOW_FEEDBACK';

/** list_recent_views — pop open the "⋯" dropdown so the list SAM reads out is also on screen. */
export const OPEN_RECENT_VIEWS_MENU = 'OPEN_RECENT_VIEWS_MENU';
/** Window event HistoryMenu listens for to open itself (fired on OPEN_RECENT_VIEWS_MENU). */
export const OPEN_RECENT_VIEWS_MENU_EVENT = 'sam:open-recent-views-menu';

/** PCR-FEAT-02 Contacts — published by search_contacts / create_contact / link_contact_to_property. */
export const OPEN_CONTACTS_PANEL = 'OPEN_CONTACTS_PANEL';
export const APPLY_CONTACTS_FILTER = 'APPLY_CONTACTS_FILTER';
export const REFRESH_CONTACTS = 'REFRESH_CONTACTS';
export const OPEN_CONTACT_DETAIL = 'OPEN_CONTACT_DETAIL';
export const REFRESH_CONTACT_DETAIL = 'REFRESH_CONTACT_DETAIL';
